// Local Active Work state — which WorkIntent THIS machine has running for a
// given repo+branch. Rides the existing CLI config root (driftlessHome() /
// DRIFTLESS_CONFIG_DIR) — deliberately NOT a second config system. Cloud is the
// source of truth for the intent itself; this file only remembers "my current
// session here" so `work finish` needs no id and the hooks know who to beat for.
import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'node:fs'
import { resolve, dirname } from 'node:path'
import { driftlessHome } from './api-client'
import { getGitRemote, getCurrentBranch } from './git'

export interface LocalWorkIntent {
  intent_id: string
  workspace: string
  repository_key: string
  branch: string
  project_id: string
  card_id: string
  objective: string
  started_at: string
  // Hook write-back bookkeeping: when we last synced observed files / beat the
  // heart, and which files were already reported (dedup without a round-trip).
  last_synced_at?: string
  reported_files?: string[]
}

type WorkStateFile = Record<string, LocalWorkIntent>

function statePath(): string {
  return resolve(driftlessHome(), 'work-intents.json')
}

// Same normalization the server applies — "Org/Repo.git" and "org/repo" are one key.
export function normalizeRepoKey(org: string, repo: string): string {
  return `${org}/${repo}`.replace(/\.git$/i, '').toLowerCase()
}

export function workStateKey(repositoryKey: string, branch: string): string {
  return `${repositoryKey}::${branch}`
}

// Detect repo key + branch from the current checkout. Null fields when git /
// remote / branch are unavailable — callers decide whether to require them.
export function detectRepoContext(): { repository_key: string | null; branch: string | null } {
  const remote = getGitRemote()
  return {
    repository_key: remote ? normalizeRepoKey(remote.org, remote.repo) : null,
    branch: getCurrentBranch(),
  }
}

function readState(): WorkStateFile {
  try {
    const p = statePath()
    if (!existsSync(p)) return {}
    const parsed = JSON.parse(readFileSync(p, 'utf8')) as WorkStateFile
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {} // a corrupt state file reads as "no local session" — never fatal
  }
}

function writeState(state: WorkStateFile): void {
  try {
    const p = statePath()
    mkdirSync(dirname(p), { recursive: true })
    writeFileSync(p, JSON.stringify(state, null, 2))
  } catch {
    /* best effort — the cloud intent is the source of truth */
  }
}

export function loadLocalWorkIntent(repositoryKey: string, branch: string): LocalWorkIntent | null {
  return readState()[workStateKey(repositoryKey, branch)] ?? null
}

export function saveLocalWorkIntent(entry: LocalWorkIntent): void {
  const state = readState()
  state[workStateKey(entry.repository_key, entry.branch)] = entry
  writeState(state)
}

export function clearLocalWorkIntent(repositoryKey: string, branch: string): void {
  const state = readState()
  delete state[workStateKey(repositoryKey, branch)]
  writeState(state)
}

// The local intent for the CURRENT checkout, or null.
export function currentLocalWorkIntent(): LocalWorkIntent | null {
  const { repository_key, branch } = detectRepoContext()
  if (!repository_key || !branch) return null
  return loadLocalWorkIntent(repository_key, branch)
}
